<template>
  <div>
    <Head :title="`${form.first_name} ${form.last_name}`" />
    <div class="flex justify-start mb-8 max-w-3xl">
      <h1 class="text-3xl font-bold">
        <Link class="text-indigo-400 hover:text-indigo-600" href="/users">{{ $t('users_edit.title') }}</Link>
        <span class="text-indigo-400 font-medium">/</span>
        {{ form.first_name }} {{ form.last_name }}
      </h1>
      <img v-if="user.photo" class="block ml-4 w-8 h-8 rounded-full" :src="user.photo" />
    </div>
    <trashed-message v-if="user.deleted_at" class="mb-6" @restore="restore"> {{ $t('users_edit.title') }}</trashed-message>
    <div class="max-w-3xl bg-white rounded-md shadow overflow-hidden">
      <form @submit.prevent="update">
        <div class="flex flex-wrap -mb-8 -mr-6 p-8">
          <text-input v-model="form.first_name" :disabled="!isAdmin" :error="form.errors.first_name" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('users_edit.form.first_name')" />
          <text-input v-model="form.last_name" :disabled="!isAdmin" :error="form.errors.last_name" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('users_edit.form.last_name')" />
          <text-input v-model="form.email" :disabled="!isAdmin" :error="form.errors.email" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('users_edit.form.email')" />
          <template v-if="isAdmin">
            <text-input v-model="form.password" :error="form.errors.password" class="pb-8 pr-6 w-full lg:w-1/2" type="password" autocomplete="new-password" :label="$t('users_edit.form.password')" />
            <select-input v-model="form.role" :error="form.errors.role" class="pb-8 pr-6 w-full lg:w-1/2" :label="$t('users_edit.form.role')">
              <option v-for="role in roles" :value="role">{{ role }}</option>
            </select-input>
            <file-input v-model="form.photo" :error="form.errors.photo" class="pb-8 pr-6 w-full lg:w-1/2" type="file" accept="image/*" :label="$t('users_edit.form.photo')" />
          </template>
        </div>
        <div class="flex items-center px-8 py-4 bg-gray-50 border-t border-gray-100" v-if="isAdmin">
          <button v-if="!user.deleted_at" class="text-red-600 hover:underline" tabindex="-1" type="button" @click="destroy">{{ $t('users_edit.form.delete') }}</button>
          <loading-button :loading="form.processing" class="btn-indigo ml-auto" type="submit">{{ $t('users_edit.form.update') }}</loading-button>
        </div>
      </form>
    </div>

    <div class="flex items-center justify-between mb-6 mt-8">
      <h1 class="text-3xl font-bold mt-4">
        {{ $t('users_edit.keys.title') }}
      </h1>
      <button type="button" class="btn-indigo" @click="destroyKeys" v-if="isSupervisor" :disabled="!deleteKeys.ids.length">
        <span>{{ $t('users_edit.keys.delete_1') }}</span>
        <span class="hidden md:inline">&nbsp;{{ $t('users_edit.keys.delete_2') }}</span>
      </button>
    </div>
    <div class="bg-white rounded-md shadow overflow-x-auto mt-4">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="pb-4 pt-6 px-6">{{ $t('users_edit.keys_table.street') }}</th>
          <th class="pb-4 pt-6 px-6">{{ $t('users_edit.keys_table.key_number') }}</th>
        </tr>
        <tr v-for="key in user.keys" :key="key.id" class="hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t">
            <span class="flex items-center px-6 py-4" tabindex="-1">
              <input
                v-if="isSupervisor"
                :ref="'key' + key.id"
                v-model="deleteKeys.ids"
                :value="key.id"
                class="mr-1"
                type="checkbox"
                style="width: 20px; height: 20px;"
              />
              {{ key.street_name }}
            </span>
          </td>
          <td class="border-t">
            <span class="flex items-center px-6 py-4" tabindex="-1">
              {{ key.key_number ?? '' }}
            </span>
          </td>
        </tr>
        <tr v-if="user.keys.length === 0">
          <td class="px-6 py-4 border-t" colspan="4">{{ $t('users_edit.keys_table.no_keys') }}</td>
        </tr>
      </table>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'
import TextInput from '@/Shared/TextInput'
import FileInput from '@/Shared/FileInput'
import SelectInput from '@/Shared/SelectInput'
import LoadingButton from '@/Shared/LoadingButton'
import TrashedMessage from '@/Shared/TrashedMessage'

export default {
  components: {
    FileInput,
    Head,
    Link,
    LoadingButton,
    SelectInput,
    TextInput,
    TrashedMessage,
  },
  layout: Layout,
  props: {
    user: Object,
    roles: Array,
  },
  remember: 'form',
  data() {
    return {
      form: this.$inertia.form({
        _method: 'put',
        first_name: this.user.first_name,
        last_name: this.user.last_name,
        email: this.user.email,
        role: this.user.role,
        password: '',
        photo: null,
      }),
      deleteKeys: this.$inertia.form({
        _method: 'post',
        user_id: this.user.id,
        ids: [],
      }),
    }
  },
  methods: {
    update() {
      this.form.post(`/users/${this.user.id}`, {
        onSuccess: () => this.form.reset('password', 'photo'),
      })
    },
    destroy() {
      if (confirm('Are you sure you want to delete this user?')) {
        this.$inertia.delete(`/users/${this.user.id}`)
      }
    },
    restore() {
      if (confirm('Are you sure you want to restore this user?')) {
        this.$inertia.put(`/users/${this.user.id}/restore`)
      }
    },
    destroyKeys() {
      if (confirm('Are you sure you want to delete keys for this user?')) {
        this.deleteKeys.post(this.route('keys.destroy'), {
          onSuccess: () => this.form.reset('ids'),
        })
      }
    },
  },
}
</script>